import { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import i18n from "../../../i18n";

const DEFAULT_PROJECT_DATA = {
  name: "",
  description: "",
  start_date: "",
  end_date: "",
  estimated_etis: "",
  budget: "",
  priority: "Medium",
  project_type: "Internal",
  status: "Planning",
  color: "#0A2368",
};

const toInputDate = (value) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toISOString().slice(0, 10);
};

const CreateEditProjectModal = ({
  showModal,
  setShowModal,
  onSubmit,
  projectData,
  setProjectData,
  isCreate = true,
  isSubmitting = false,
}) => {
  const [dateValidationError, setDateValidationError] = useState("");

  const safeProjectData = useMemo(
    () => ({
      ...DEFAULT_PROJECT_DATA,
      ...projectData,
      start_date: toInputDate(projectData?.start_date),
      end_date: toInputDate(projectData?.end_date),
    }),
    [projectData]
  );

  useEffect(() => {
    if (!showModal) {
      setDateValidationError("");
    }
  }, [showModal]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setProjectData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    setShowModal(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (safeProjectData.name.trim().length < 3) {
      setDateValidationError("Project must have a name with at least 3 characters.");
      return;
    }

    if (safeProjectData.start_date && safeProjectData.end_date && safeProjectData.end_date < safeProjectData.start_date) {
      setDateValidationError("End date must be later than start date.");
      return;
    }

    if (safeProjectData.description.length > 10000) {
      setDateValidationError("Description should not surpass 10000 characters.");
      return;
    }

    if (safeProjectData.budget < 0) {
      setDateValidationError("Budget should not be negative.");
      return;
    }

    setDateValidationError("");

    const payload = {
      ...safeProjectData,
      estimated_etis:
        safeProjectData.estimated_etis === "" ? null : Number(safeProjectData.estimated_etis),
      budget: safeProjectData.budget === "" ? null : Number(safeProjectData.budget),
    };

    onSubmit(payload);
  };

  return (
    <Modal show={showModal} onHide={handleClose} centered size="lg" dialogClassName="project-modal-dialog">
      <Modal.Header closeButton>
        <Modal.Title>
          {isCreate
            ? i18n.t("components.CreateEditProjectModal.create_project")
            : i18n.t("components.CreateEditProjectModal.edit_project")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit} noValidate>
          <Row>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.Project.name")}</Form.Label>
                <Form.Control
                  name="name"
                  value={safeProjectData.name}
                  placeholder={i18n.t("fields.Project.name_placeholder")}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.Project.description")}</Form.Label>
                <Form.Control
                  name="description"
                  as="textarea"
                  rows={3}
                  placeholder={i18n.t("fields.Project.description_placeholder")}
                  value={safeProjectData.description}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.Project.start_date")}</Form.Label>
                <Form.Control
                  name="start_date"
                  placeholder={i18n.t("fields.Project.start_date_placeholder")}
                  type="date"
                  value={safeProjectData.start_date}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.Project.end_date")}</Form.Label>
                <Form.Control
                  name="end_date"
                  placeholder={i18n.t("fields.Project.end_date_placeholder")}
                  type="date"
                  value={safeProjectData.end_date}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.Project.estimated_etis")}</Form.Label>
                <Form.Control
                  name="estimated_etis"
                  placeholder={i18n.t("fields.Project.estimated_etis_placeholder")}
                  type="number"
                  min="0"
                  step="0.01"
                  value={safeProjectData.estimated_etis}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.Project.budget")}</Form.Label>
                <Form.Control
                  name="budget"
                  placeholder={i18n.t("fields.Project.budget_placeholder")}
                  type="number"
                  min="0"
                  step="0.01"
                  value={safeProjectData.budget}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={3}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.Project.priority")}</Form.Label>
                <Form.Select
                  name="priority"
                  value={safeProjectData.priority}
                  onChange={handleInputChange}
                >
                  <option value="Low">{i18n.t("fields.Project.priority_enum.low")}</option>
                  <option value="Medium">{i18n.t("fields.Project.priority_enum.medium")}</option>
                  <option value="High">{i18n.t("fields.Project.priority_enum.high")}</option>
                  <option value="Urgent">{i18n.t("fields.Project.priority_enum.urgent")}</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12} md={3}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.Project.project_type")}</Form.Label>
                <Form.Select
                  name="project_type"
                  value={safeProjectData.project_type}
                  onChange={handleInputChange}
                >
                  <option value="Financed">{i18n.t("fields.Project.project_type_enum.financed")}</option>
                  <option value="Client">{i18n.t("fields.Project.project_type_enum.client")}</option>
                  <option value="Internal">{i18n.t("fields.Project.project_type_enum.internal")}</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.Project.status")}</Form.Label>
                <Form.Select
                  name="status"
                  value={safeProjectData.status}
                  onChange={handleInputChange}
                >
                  <option value="Planning">{i18n.t("fields.Project.status_enum.planning")}</option>
                  <option value="Active">{i18n.t("fields.Project.status_enum.active")}</option>
                  <option value="Completed">{i18n.t("fields.Project.status_enum.completed")}</option>
                  <option value="Archived">{i18n.t("fields.Project.status_enum.archived")}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={3}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.Project.color", "Cor do Projeto")}</Form.Label>
                <Form.Control
                  name="color"        
                  type="color"         
                  value={safeProjectData.color}
                  onChange={handleInputChange}
                  style={{ width: '100%', height: '38px', padding: '0.2rem', cursor: 'pointer' }}
                />
              </Form.Group>
            </Col>
          </Row>

          {dateValidationError && <p className="text-danger small mb-3">{dateValidationError}</p>}

          <div className="d-flex justify-content-end gap-2 mt-2">
            <Button variant="outline-secondary" onClick={handleClose} disabled={isSubmitting}>
              {i18n.t("common.actions.cancel")}
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? "Saving..."
                : isCreate
                  ? i18n.t("common.actions.create")
                  : i18n.t("common.actions.update")}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateEditProjectModal;
